import type { CoolingSystemConfigurationsSaveHandle } from './subsections/coolingSystemConfigurationsSaveHandle';
import type { SelectedFile } from '../../../components/forms/FileUpload';
import type { RepeatableInstanceSaveHandle } from '../../../components/repeatableSections/repeatableInstanceSaveHandle';
import type { SavedAttachment } from '../../../services/attachmentService';
import type { Request } from '../../../types';
import type { CoolingSystemConfiguration } from '../../../types/coolingSystem';

export interface CoolingInitialProps {
  formData: Record<string, unknown>;
  updateFormData: (
    field: string,
    value: string | number | boolean | CoolingSystemConfiguration[] | undefined,
  ) => void;
  requestId?: string;
  disabled?: boolean;
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  isEditing?: boolean;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  offlineMode?: boolean;
  registerCoolingSystemSaveHandle?: (
    handle: CoolingSystemConfigurationsSaveHandle | null,
  ) => void;
  onCoolingSystemEditorActiveChange?: (active: boolean) => void;
}

export interface CoolingPersistedProps {
  request: Request;
  isEditing: boolean;
  editedData: Record<string, unknown>;
  updateEditedData: (
    field: string,
    value: string | number | boolean | undefined,
  ) => void;
  showFullDetails?: boolean;
  showViewToggle?: boolean;
  isAdvancedView?: boolean;
  onToggleView?: () => void;
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  offlineMode?: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
  /**
   * Request Detail engine wiring (#997 Story 6.6). The engine section registers
   * its save handle with the screen's `useCoolingSystemEngineSaveBridge` and
   * writes the live session list into `editedData.coolingSystemConfigurations`
   * via `applyEditedDataPatch` so it survives step navigation.
   */
  registerInstanceSaveHandle?: (
    handle: RepeatableInstanceSaveHandle | null,
  ) => void;
  applyEditedDataPatch?: (patch: Record<string, unknown>) => void;
  /** True when `editedData.coolingSystemConfigurations` was touched this edit session. */
  coolingSystemConfigurationsDirty?: boolean;
  onCoolingSystemEditorActiveChange?: (active: boolean) => void;
}

export type CoolingProps = CoolingInitialProps | CoolingPersistedProps;

export function isCoolingPersistedProps(
  p: CoolingProps,
): p is CoolingPersistedProps {
  return 'request' in p && (p as CoolingPersistedProps).request != null;
}
